import {
  DeliveryOrder,
  DeliveryOrderStatus,
  OrderWithDeliveryOrders,
  PendingItem,
} from '@diamond/shared/types';
import { SOStatus } from '@diamond/sol-buyer/constant';

type OrderWithOptionalStatus = OrderWithDeliveryOrders & {
  status?: 'waiting_for_payment' | 'submitted' | string;
};

/**
 * Derives SO (Sales Order) status from DO (Delivery Order) statuses
 * Following TRD Status Transition Rules
 * Returns a SOStatus that maps to SO_STATUS_CONFIG labels
 *
 * TRD Key Rules:
 * - delivered (Dikirim) = NOT terminal, means "in transit"
 * - received/partially_received/rejected/cancelled = terminal
 * - If any DO is delivered AND any is cancelled -> Dikirim Sebagian
 * - If all DOs are terminal -> Selesai or Selesai Sebagian
 */
export function deriveOrderStatus(
  order: OrderWithOptionalStatus,
  deliveryOrders?: DeliveryOrder[],
  pendingItems?: PendingItem[]
): SOStatus {
  const doList = deliveryOrders || order.delivery_orders || [];
  const pendingList = pendingItems || order.pending_items || [];

  // TRD Rule: Before SO Created - Menunggu Konfirmasi
  if (doList.length === 0) {
    if (order.status === 'waiting_for_payment') {
      return 'pending';
    }
    if (order.status === 'submitted') {
      return 'waiting_confirmation';
    }
    return pendingList.length > 0 ? 'waiting_confirmation' : 'processing';
  }

  // Count DO statuses for TRD rules
  const hasProcessed = doList.some((d) => d.status === 'processed');
  const hasDelivered = doList.some((d) => d.status === 'delivered');
  const hasCancelled = doList.some((d) => d.status === 'cancelled');

  // TRD: Only 'received', 'partially_received', 'rejected', 'cancelled' are terminal
  // 'delivered' is NOT terminal - it's "in transit" (Dikirim)
  const terminalStatuses: DeliveryOrderStatus[] = [
    'received',
    'partially_received',
    'rejected',
    'cancelled',
  ];
  const allTerminal = doList.every((d) => terminalStatuses.includes(d.status));

  // Check for partial delivery: multiple DOs with some delivered, some cancelled
  // This means some items are in transit while others are cancelled
  const hasPartialDelivery = doList.length > 1 && hasDelivered && hasCancelled;

  // TRD Rule: At least 1 DO is Dikirim -> Dikirim
  // If also has cancelled DOs -> Dikirim Sebagian
  if (hasDelivered) {
    if (hasPartialDelivery) {
      return 'partial_delivering';
    }
    return 'delivering';
  }

  // TRD Rule: All items have status Dikirim/Dibatalkan/Ditolak -> Selesai
  if (allTerminal) {
    return pendingList.length > 0 ? 'partial_completed' : 'completed';
  }

  // TRD Rule: Dikemas AND at least 1 DO is Dibatalkan -> Diproses Sebagian
  if (hasProcessed && hasCancelled) {
    return 'partial_processing';
  }

  // TRD Rule: SO Created OR at least 1 DO is Dikemas -> Diproses
  return 'processing';
}
